import React, { useState, useEffect } from "react";
import { Box, Container, Typography, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';

const HeroSlider = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const prefix = process.env.PUBLIC_URL || '';
  const images = [
    `${prefix}/bgImages/Tel-aviv-highway.jpeg`,
    `${prefix}/bgImages/Jerusalem.jpeg`,
    `${prefix}/bgImages/Eilat.jpeg`,
    `${prefix}/bgImages/natanya.jpeg`,
    `${prefix}/bgImages/bircat-ram.jpeg`,
    `${prefix}/bgImages/palm-trees.jpeg`,
  ];

  // Change d'image toutes les 5 secondes avec transition douce
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex(
        (prevIndex) => (prevIndex + 1) % images.length
      );
    }, 5000);
    return () => clearInterval(timer);
  }, [images.length]);

  return (
    <Box
      sx={{
        position: "relative",
        height: "696px", // Hauteur fixe comme demandé
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        color: "white",
      }}
    >
      {/* Images de fond avec transition fade douce */}
      {images.map((image, index) => (
        <Box
          key={index}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundImage: `url('${image}')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            opacity: currentImageIndex === index ? 1 : 0,
            transition: "opacity 2s ease-in-out",
            zIndex: 0,
          }}
        />
      ))}

      {/* Gradient overlay plus étendu comme dans l'image */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: { xs: "100%", md: "50%" },
          height: "100%",
          background: { 
            xs: `linear-gradient(180deg, 
              rgba(12, 48, 88, 0.3) 0%, 
              rgba(14, 52, 93, 0.5) 30%, 
              rgba(17, 56, 98, 0.7) 60%, 
              rgba(12, 48, 88, 0.9) 100%
            )`,
            md: `linear-gradient(270deg, 
              rgba(12, 48, 88, 0) 0%, 
              rgba(14, 52, 93, 0.3) 20%, 
              rgba(17, 56, 98, 0.6) 40%, 
              rgba(12, 48, 88, 0.8) 60%, 
              rgba(5, 32, 60, 0.95) 100%
            )`
          },
          zIndex: 1,
        }}
      />

      {/* Contenu principal avec image israel.svg */}
      <Container maxWidth="" sx={{ position: "relative", zIndex: 2, height: "100%" }}>
        <Box 
          sx={{ 
            maxWidth: { xs: "90%", sm: "80%", md: "600px", lg: "700px" }, 
            pl: { xs: 0, sm: 3, md: 4 },
            pr: { xs: 0, sm: 3, md: 2 },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            height: "100%",
            minHeight: "500px"
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "left",
              mb: { xs: 2, md: 3 },
              gap: 1,
            }}
          >
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/israel.svg`}
              alt="Israel Map"
              sx={{
                width: { xs: "50px", md: "60px" },
                height: "auto",
                filter: "drop-shadow(2px 2px 4px rgba(0,0,0,0.3))",
              }}
            />
            <Typography variant="stateLabel">
              {t('header.stateOfIsrael')}
            </Typography>
          </Box>
          
          <Typography
            variant="heroMainTitle"
            component="h1"
            sx={{ mb: { xs: 1, md: 2 } }}
          >
            {t('hero.welcome')}
          </Typography>

          <Typography
            variant="heroSubtitle"
            component="h2"
            sx={{ mb: { xs: 2, md: 3 } }}
          >
            {t('hero.subtitle')}
          </Typography>

          {/* Texte adaptatif selon la longueur */}
          <Box sx={{ mb: { xs: 3, md: 4 } }}>
            <Typography 
              component="p" 
              variant="heroText" 
              sx={{ 
                mb: 2,
                lineHeight: { xs: 1.4, md: 1.5 },
                fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' }
              }}
            >
              {t('hero.description1')}
            </Typography>

            <Typography 
              component="p" 
              variant="heroText" 
              sx={{ 
                lineHeight: { xs: 1.4, md: 1.5 },
                fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' }
              }}
            >
              {t('hero.description2')}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
            <Button
              variant="hero"
              size="large"
              onClick={() => navigate("/apply-for-an-eta-il-1")}
              sx={{
                px: { xs: 3, md: 4 },
                py: { xs: 1.2, md: 1.5 },
                fontSize: { xs: '0.9rem', md: '1rem' },
                '&:focus': {
                  outline: '2px solid white',
                  outlineOffset: '2px',
                },
                '&:focus-visible': {
                  outline: '2px solid white',
                  outlineOffset: '2px',
                }
              }}
            >
              {t('hero.buttonText')}
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default HeroSlider;
